/**
 * WAGE PASS-THROUGH MODEL
 * 
 * Determines how much of the employer premium savings are passed through to workers as increased wages.
 * Based on economic evidence that pass-through is not 100% and varies by sector power dynamics.
 * 
 * Source: SAFECare Methodology (Section 202) & CBO/Urban Institute Elasticities
 */

export const WAGE_PASSTHROUGH_PARAMS = {
    // Phase-in parameters matched to bill implementation
    phaseIn: {
        union_mandatory: {
            2026: 0.00,
            2027: 0.25,
            2028: 0.55,
            2029: 0.70,
            2030: 0.75  // Steady state: 75% capture
        },
        public_sector: {
            2026: 0.00,
            2027: 0.10,
            2028: 0.30,
            2029: 0.45,
            2030: 0.50  // Steady state: 50% capture (budget constraints)
        },
        private_voluntary: {
            2026: 0.00,
            2027: 0.05,
            2028: 0.10,
            2029: 0.15,
            2030: 0.22, // Steady state: 22% capture (weaker bargaining)
            2032: 0.22
        }
    }
};

/**
 * Calculate the portion of employer premium savings passed to the worker.
 * 
 * @param {Object} household - Household data object
 * @param {number} premiumSavings - Total dollar amount of employer premium saved
 * @param {number} year - Simulation year
 * @returns {Object} - Result with wageIncrease and metadata
 */
export function calculateWagePassThrough(household, premiumSavings, year) {
    if (!premiumSavings || premiumSavings <= 0) {
        return { wageIncrease: 0, rate: 0, applied: false };
    }

    if (household.is_self_employed) {
        return { wageIncrease: 0, rate: 0, sector: 'self_employed', applied: false };
    }

    // Determine Sector & Rate
    let passthroughRate = 0;
    let sector = 'private_voluntary';

    // Check sector attributes (if available in synthetic data)
    if (household.union_member || household.union_probability > 0.5) {
        sector = 'union_mandatory';
        passthroughRate = getRateForYear(WAGE_PASSTHROUGH_PARAMS.phaseIn.union_mandatory, year); // ~0.75
    } else if (household.public_sector_employee || household.public_sector_share > 0.5) {
        sector = 'public_sector';
        passthroughRate = getRateForYear(WAGE_PASSTHROUGH_PARAMS.phaseIn.public_sector, year); // ~0.50
    } else {
        sector = 'private_voluntary';
        passthroughRate = getRateForYear(WAGE_PASSTHROUGH_PARAMS.phaseIn.private_voluntary, year); // ~0.22
    }

    const wageIncrease = premiumSavings * passthroughRate;

    return {
        wageIncrease,
        rate: passthroughRate,
        sector,
        applied: true
    };
}

/**
 * Helper to get rate for specific year, handling interpolation or clamping if needed.
 * Simple lookup for now.
 */
function getRateForYear(schedule, year) {
    if (schedule[year] !== undefined) return schedule[year];

    // Find closest years
    const years = Object.keys(schedule).map(Number).sort((a, b) => a - b);

    if (year < years[0]) return 0;
    if (year > years[years.length - 1]) return schedule[years[years.length - 1]];

    // Fallback: search backwards
    for (let i = years.length - 1; i >= 0; i--) {
        if (year > years[i]) return schedule[years[i]];
    }

    return 0;
}
